// src/services/api.js
import { getApiUrl, API_CONFIG } from '../config.js';

class ApiService {
    constructor() {
        this.token = localStorage.getItem('token');
    }

    /**
     * CRITICAL: Store token in localStorage
     * Called after successful login
     */
    setToken(token) {
        this.token = token;
        try {
            localStorage.setItem('token', token);
            // Verify it was actually stored
            const stored = localStorage.getItem('token');
            if (stored === token) {
                console.log('[API] ✅ Token successfully stored and verified in localStorage');
            } else {
                console.error('[API] ❌ ERROR: Token stored but verification failed!');
                console.error('[API] Stored:', stored ? stored.substring(0, 30) + '...' : 'null');
                console.error('[API] Expected:', token.substring(0, 30) + '...');
            }
        } catch (e) {
            console.error('[API] ❌ ERROR: Failed to store token in localStorage:', e.message);
            console.error('[API] localStorage disabled or full?');
        }
    }

    /**
     * Clear token (on logout or 401)
     */
    clearToken() {
        this.token = null;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('userData');
    }


    

    /**
     * Build Authorization header with Bearer token
     * Reads fresh token from localStorage each time
     * SKIP for auth endpoints (login, register, etc)
     */
    getAuthHeaders(endpoint = '') {
        const headers = {
            'Content-Type': 'application/json',
        };

        // Don't send Authorization header for auth-related endpoints
        // This prevents old/expired tokens from interfering with login/register/verify-email
        const authEndpoints = ['/auth/login', '/auth/register', '/auth/verify-email', '/auth/provider-login', '/auth/google'];
        const isAuthEndpoint = authEndpoints.some(authEndpoint => endpoint.includes(authEndpoint));
        
        if (isAuthEndpoint) {
            return headers; // Don't add Authorization header for auth endpoints
        }

        // CRITICAL: Always read fresh token from localStorage
        const token = localStorage.getItem('token');
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
            console.debug('[API] Token added to headers for endpoint:', endpoint);
        } else {
            console.warn('[API] ⚠️ No token found in localStorage for endpoint:', endpoint);
            console.warn('[API] Available localStorage keys:', Object.keys(localStorage).join(', '));
        }

        return headers;
    }
        

    /**
     * Generic HTTP request handler
     * Automatically attaches JWT to all requests
     */
    async request(endpoint, method = 'GET', data = null) {
        const url = getApiUrl(endpoint);
        const options = {
            method,
            headers: this.getAuthHeaders(endpoint),
            // CRITICAL: Disable caching for API requests to prevent 304 responses
            cache: 'no-store',
        };

        if (data && ['POST', 'PUT', 'PATCH'].includes(method)) {
            // Handle FormData (for file uploads)
            if (data instanceof FormData) {
                // Don't set Content-Type header - browser will set it automatically with boundary
                delete options.headers['Content-Type'];
                options.body = data;
            } else {
                // Handle regular JSON data
                options.body = JSON.stringify(data);
            }
        }

        try {
            const response = await fetch(url, options);

            // Handle 304 Not Modified: Reject so we force a fresh request
            if (response.status === 304) {
                console.warn('[API] Got 304 Not Modified - server cache headers misconfigured');
                throw new Error('Received cached response - please refresh');
            }

            // Parse response JSON (needed for all error handling)
            let result;
            try {
                result = await response.json();
            } catch (e) {
                console.error('[API] Failed to parse response JSON:', e.message);
                result = { message: response.statusText, error: 'Failed to parse response' };
            }

            // Handle 401: Clear token and redirect to login
            if (response.status === 401) {
                const hasToken = !!localStorage.getItem('token');
                const isLoginPage = ['/login', '/provider/login'].includes(window.location.pathname);

                if (hasToken && !isLoginPage && !endpoint.includes('/auth/')) {
                    // Detect whether this was a provider session before clearing
                    let isProvider = false;
                    try {
                        const userData = localStorage.getItem('userData');
                        if (userData) {
                            const parsed = JSON.parse(userData);
                            isProvider = parsed.role === 'provider' || (Array.isArray(parsed.roles) && parsed.roles.includes('provider'));
                        }
                    } catch { /* ignore */ }
                    this.clearToken();
                    window.location.href = isProvider ? '/provider/login' : '/login';
                }
                throw new Error('Unauthorized: ' + (result?.message || 'Session expired'));
            }

            if (!response.ok) {
                console.error('[API] Request failed:', {
                    status: response.status,
                    endpoint,
                    message: result?.message || result?.error,
                    details: result
                });
                const error = new Error(result?.message || result?.error || `Request failed (${response.status})`);
                error.status = response.status;
                error.endpoint = endpoint;
                error.validationErrors = result?.errors || null;
                error.fullResponse = result;
                throw error;
            }

            return result;
        } catch (error) {
            console.error('[API] Error:', error);
            throw error;
        }
    }

    // ===== AUTH METHODS =====

    async login(credentials) {
        this.clearToken();
        const result = await this.request('/auth/login', 'POST', credentials);
        if (result.success && result.data?.token) {
            this.setToken(result.data.token);
        }
        return result;
    }

    async logout() {
        try {
            await this.request('/auth/logout', 'POST');
        } finally {
            this.clearToken();
        }
    }

    isLoggedIn() {
        return !!localStorage.getItem('token');
    }

    // ===== HTTP METHODS =====

    async get(endpoint) {
        return this.request(endpoint, 'GET');
    }

    async post(endpoint, data) {
        return this.request(endpoint, 'POST', data);
    }

    async put(endpoint, data) {
        return this.request(endpoint, 'PUT', data);
    }

    async patch(endpoint, data) {
        return this.request(endpoint, 'PATCH', data);
    }

    async delete(endpoint) {
        return this.request(endpoint, 'DELETE');
    }

    // ===== SPECIFIC ENDPOINTS =====

    async getDashboardStats() {
    return this.get('/api/customer/dashboard/stats');
}

    

    // Keep existing methods for backward compatibility
    async register(userData) {
        return this.request('/auth/register', 'POST', userData);
    }

    async getProfile() {
        return this.request('/auth/profile', 'GET');
    }

    async getBookings() {
        return this.request('/bookings', 'GET');
    }

    async createBooking(bookingData) {
        return this.request('/bookings', 'POST', bookingData);
    }

    async updateBooking(bookingId, updateData) {
        return this.request(`/bookings/${bookingId}`, 'PUT', updateData);
    }

    async cancelBooking(bookingId) {
        return this.request(`/bookings/${bookingId}/cancel`, 'POST');
    }
        // Get all services
        async getServices() {
                return this.request(API_CONFIG.ENDPOINTS.SERVICES, 'GET');
        }

        // Get service categories
        async getServiceCategories() {
                return this.request(API_CONFIG.ENDPOINTS.SERVICE_CATEGORIES, 'GET');
        }

        // Get property types
        async getPropertyTypes() {
                return this.request(API_CONFIG.ENDPOINTS.PROPERTY_TYPES, 'GET');
        }

        // Get sectors
        async getSectors() {
                return this.request(API_CONFIG.ENDPOINTS.SECTORS, 'GET');
        }

        // ============ CONTACT API METHODS ============

        // Send contact message
        async sendContactMessage(messageData) {
                return this.request(API_CONFIG.ENDPOINTS.CONTACT, 'POST', messageData);
        }

        // Get contact info
        async getContactInfo() {
                return this.request(API_CONFIG.ENDPOINTS.CONTACT_INFO, 'GET');
        }

        // ============ QUOTES API METHODS ============

        // Calculate quote estimate
        async calculateQuote(quoteData) {
                return this.request(API_CONFIG.ENDPOINTS.CALCULATE_QUOTE, 'POST', quoteData);
        }

        // Create quote
        async createQuote(quoteData) {
                return this.request(API_CONFIG.ENDPOINTS.QUOTES, 'POST', quoteData);
        }

        // Get user quotes
        async getUserQuotes() {
                return this.request(API_CONFIG.ENDPOINTS.QUOTES, 'GET');
        }

        // ============ UTILITY METHODS ============

        // Health check
        async healthCheck() {
                return this.request(API_CONFIG.ENDPOINTS.HEALTH, 'GET');
        }

        // Database info (for debugging)
        async getDatabaseInfo() {
                return this.request(API_CONFIG.ENDPOINTS.DATABASE_INFO, 'GET');
        }
}

// Export singleton instance
export default new ApiService();